import { Button, Checkbox, Field, Input } from '@bb/ui';
import { DurationField } from '../../../../components/DurationField.tsx';
import { TriggerList } from '../../../campaigns/trigger/TriggerEditor.tsx';
import {
  ACTION_PATHS_MAX_GROUPS,
  ACTION_PATHS_MAX_TRIGGERS,
  actionPathsIssues,
  newGroup,
  readActionPaths,
  withExit,
  type ActionPathsConfig,
  type ActionPathsGroup,
} from './actionPathsConfig.ts';

export interface ActionPathsPanelProps {
  environmentId: string;
  /** The step's `config` as stored. */
  value: unknown;
  /** Every edit, with the server's defaults filled in; the editor reconciles the step's ports. */
  onChange: (next: ActionPathsConfig) => void;
  readOnly?: boolean;
  /** The step is in the running version: the order and the set of groups (its ports) cannot change. */
  locked?: boolean;
}

const EVERYONE_ELSE = 'Everyone Else';

function Errors({ messages }: { messages: Array<string | undefined> }) {
  return (
    <>
      {messages.filter(Boolean).map((message) => (
        <p key={message} className="bb-field__error" role="alert">
          {message}
        </p>
      ))}
    </>
  );
}

/**
 * Settings of an Action Paths step (BB-117): the window users wait in, the groups in order — each with the actions
 * (BB-100 triggers) that take a user down its path or out of the Canvas — and Everyone Else, always last, for users who
 * did nothing in the window. Ranking is not built yet, so it stays off. A running Canvas keeps the order and the set
 * of groups (`locked`, the BB-121 lock of paths steps).
 */
export function ActionPathsPanel({ environmentId, value, onChange, readOnly = false, locked = false }: ActionPathsPanelProps) {
  const config = readActionPaths(value);
  const issues = actionPathsIssues(config);
  const structural = !readOnly && !locked;
  // Keys follow the groups when they move; an odd stored config with repeated ids falls back to positions.
  const uniqueIds = new Set(config.groups.map((g) => g.id)).size === config.groups.length;
  const setGroup = (index: number, next: ActionPathsGroup) => onChange({ ...config, groups: config.groups.map((g, i) => (i === index ? next : g)) });
  const move = (index: number, delta: number) => {
    const groups = [...config.groups];
    const [g] = groups.splice(index, 1);
    groups.splice(index + delta, 0, g!);
    onChange({ ...config, groups });
  };

  return (
    <section className="step-panel" aria-label="Action Paths settings">
      <p className="hint">Users wait up to the window for any group’s action. The first action that matches a group takes them down its path at once; users who did nothing go to {EVERYONE_ELSE} when the window ends.</p>
      <DurationField
        label="Window"
        seconds={config.windowSeconds}
        units={['minutes', 'hours', 'days']}
        error={issues.fields.windowSeconds}
        hint="Up to 31 days."
        disabled={readOnly}
        onChange={(windowSeconds) => onChange({ ...config, windowSeconds })}
      />
      <fieldset className="step-panel__ranking">
        <legend>Ranking</legend>
        <Checkbox label="Rank the groups" checked={config.ranking} disabled={readOnly || !config.ranking} onCheckedChange={(on) => onChange({ ...config, ranking: on })} />
        <p className="hint">
          Not available yet. Without ranking, the first group whose action a user performs takes them right away, and a later action of another group does not move them. Ranking — holding users to the end of the window and taking the highest group they performed — comes with BB-117 T4 once VCG needs it.
        </p>
        <Errors messages={[issues.fields.ranking]} />
      </fieldset>
      {locked && <p className="hint">The Canvas is running: the order and the set of groups cannot change.</p>}
      <ol className="audience-paths__groups">
        {config.groups.map((group, index) => {
          const own = issues.groups[index]!;
          return (
            <li key={uniqueIds ? group.id : `${group.id}-${index}`}>
              <section aria-label={`Group ${index + 1}: ${group.name}`} className="step-panel__group">
                <div className="filters">
                  <Field label="Group name" error={own.name}>
                    <Input value={group.name} maxLength={100} disabled={readOnly} onChange={(e) => setGroup(index, { ...group, name: e.target.value })} />
                  </Field>
                  {structural && (
                    <>
                      <Button size="sm" variant="ghost" aria-label={`Move group ${group.name} up`} disabled={index === 0} onClick={() => move(index, -1)}>
                        ↑
                      </Button>
                      <Button size="sm" variant="ghost" aria-label={`Move group ${group.name} down`} disabled={index === config.groups.length - 1} onClick={() => move(index, 1)}>
                        ↓
                      </Button>
                      <Button size="sm" variant="ghost" aria-label={`Remove group ${group.name}`} disabled={config.groups.length === 1} onClick={() => onChange({ ...config, groups: config.groups.filter((_, i) => i !== index) })}>
                        Remove
                      </Button>
                    </>
                  )}
                </div>
                <p className="hint">Path: {group.id}</p>
                <Errors messages={[own.id]} />
                <label>
                  <input type="checkbox" checked={Boolean(group.exitAfter)} disabled={!structural} onChange={(e) => setGroup(index, withExit(group, e.target.checked))} /> End the Canvas for this group
                </label>
                <p className="hint">Any of these actions takes the user.</p>
                <TriggerList
                  environmentId={environmentId}
                  readOnly={readOnly}
                  issues={own.triggers}
                  list="triggers"
                  items={group.triggers}
                  onChange={(triggers) => setGroup(index, { ...group, triggers })}
                  itemLabel="Action"
                  addLabel="Add an action"
                  min={1}
                  max={ACTION_PATHS_MAX_TRIGGERS}
                />
              </section>
            </li>
          );
        })}
      </ol>
      <Errors messages={[issues.fields.groups]} />
      {structural && config.groups.length < ACTION_PATHS_MAX_GROUPS && (
        <Button size="sm" onClick={() => onChange({ ...config, groups: [...config.groups, newGroup(config.groups)] })}>
          Add group
        </Button>
      )}
      <section aria-label={EVERYONE_ELSE} className="audience-paths__else">
        <h4>{EVERYONE_ELSE}</h4>
        <p className="hint">Users who performed no group’s action within the window. Always the last branch.</p>
        <label>
          <input type="checkbox" checked={Boolean(config.everyoneElse.exitAfter)} disabled={!structural} onChange={(e) => onChange({ ...config, everyoneElse: withExit(config.everyoneElse, e.target.checked) })} /> End the Canvas for everyone else
        </label>
      </section>
    </section>
  );
}
